<section class="employee-workspace-modal">
  <header class="employee-workspace__hero">
    <section class="employee-workspace-modal__header">
      <div class="employee-workspace__header-main">
        <div class="employee-workspace__identity">
          <div class="employee-workspace__avatar">
            @if (user().image) {
              <img [src]="user().image"
                [alt]="user().displayName" />
            } @else {
              <ion-icon name="person-circle-outline"></ion-icon>
            }
          </div>

          <div class="employee-workspace__identity-copy">
            <div class="employee-workspace__name-row">
              <h1>{{ user().displayName }}</h1>
              <div class="employee-workspace__compact-chips">
                <span class="employee-workspace__compact-chip">{{ personTypeLabel() }}</span>
                <span class="employee-workspace__compact-chip">{{ teamLabel() }}</span>
                <span class="employee-workspace__compact-chip">{{ areaLabel() }}</span>
              </div>
            </div>
            <p class="employee-workspace__meta">{{ roleLabel() }} · {{ emailLabel() }}</p>
          </div>
        </div>
      </div>

      <div class="employee-workspace__actions">
        <button class="employee-workspace__nav-button employee-workspace__close profile-button profile-button--ghost"
          type="button"
          (click)="dismiss()"
          [attr.aria-label]="'users.workspace.actions.close' | translate">
          <ion-icon name="close-outline"></ion-icon>
        </button>
      </div>
    </section>
  </header>

  <section class="employee-workspace">
    <app-segment-list class="employee-workspace__tabs"
      [segmentValue]="workspaceTab()"
      [segmentButtons]="workspaceTabs()"
      [sub]="true"
      (segmentChanged)="onWorkspaceTabChanged($event)"></app-segment-list>

    <div class="employee-workspace__content">
      <div class="employee-workspace__panel">
        @switch (workspaceTab()) {
          @case ('overview') {
            <div class="employee-workspace__grid">
              <article class="employee-card">
                <div class="employee-card__header">
                  <h2>{{ 'users.workspace.overview.basic.title' | translate }}</h2>
                  <p>{{ 'users.workspace.overview.basic.description' | translate }}</p>
                </div>

                <div class="employee-card__details">
                  <div>
                    <span>{{ 'users.workspace.labels.name' | translate }}</span>
                    <strong>{{ displayNameLabel() }}</strong>
                  </div>
                  <div>
                    <span>{{ 'users.role' | translate }}</span>
                    <strong>{{ roleLabel() }}</strong>
                  </div>
                  <div>
                    <span>{{ 'users.directory.filters.status' | translate }}</span>
                    <strong>{{ statusLabel() }}</strong>
                  </div>
                  <div>
                    <span>{{ 'users.directory.filters.team' | translate }}</span>
                    <strong>{{ teamLabel() }}</strong>
                  </div>
                  <div>
                    <span>{{ 'users.directory.filters.area' | translate }}</span>
                    <strong>{{ areaLabel() }}</strong>
                  </div>
                  <div>
                    <span>{{ 'users.company' | translate }}</span>
                    <strong>{{ companyLabel() }}</strong>
                  </div>
                  <div>
                    <span>{{ 'users.email' | translate }}</span>
                    <strong>{{ emailLabel() }}</strong>
                  </div>
                  <div>
                    <span>{{ 'users.phone' | translate }}</span>
                    <strong>{{ phoneLabel() }}</strong>
                  </div>
                </div>
              </article>

              <article class="employee-card">
                <div class="employee-card__header">
                  <h2>{{ 'users.workspace.overview.now.title' | translate }}</h2>
                  <p>{{ 'users.workspace.overview.now.description' | translate }}</p>
                </div>

                <div class="employee-card__details employee-card__details--summary">
                  <div>
                    <span>{{ 'users.workspace.labels.activity' | translate }}</span>
                    <strong>{{ statusLabel() }}</strong>
                  </div>
                  <div>
                    <span>{{ 'users.workspace.labels.nextShift' | translate }}</span>
                    @if (nextShiftDisplay$ | async; as nextShift) {
                      <strong>{{ nextShift.date }} · {{ nextShift.siteName }}</strong>
                      <small>{{ nextShift.timeRangeLabel }}</small>
                    } @else {
                      <strong>{{ 'users.workspace.fallbacks.noUpcomingShift' | translate }}</strong>
                      <small>{{ 'users.workspace.fallbacks.noScheduledShifts' | translate }}</small>
                    }
                  </div>
                  <div>
                    <span>{{ 'users.workspace.labels.currentAllocation' | translate }}</span>
                    <strong>{{ (currentAssignment$ | async)?.name || ('users.workspace.fallbacks.noCurrentAllocation' | translate) }}</strong>
                    <small>
                      @if (currentAssignment$ | async; as currentAssignment) {
                        {{ currentAssignment.upcomingCount }}
                        {{ 'users.workspace.fallbacks.upcomingShifts' | translate }}
                      } @else {
                        {{ 'users.workspace.fallbacks.noSelectedSite' | translate }}
                      }
                    </small>
                  </div>
                </div>
              </article>

              <article class="employee-card employee-card--full">
                <div class="employee-card__header">
                  <h2>{{ 'users.workspace.overview.summary.title' | translate }}</h2>
                  <p>{{ 'users.workspace.overview.summary.description' | translate }}</p>
                </div>

                @if (overviewStats$ | async; as stats) {
                  <div class="employee-stats">
                    @for (stat of stats; track stat.label) {
                      <div class="employee-stat">
                        <span>{{ stat.label }}</span>
                        <strong>{{ stat.value }}</strong>
                        <small>{{ stat.detail }}</small>
                      </div>
                    }
                  </div>
                }
              </article>

              <article class="employee-card employee-card--full">
                <div class="employee-card__header">
                  <h2>{{ 'users.workspace.overview.activity.title' | translate }}</h2>
                  <p>{{ 'users.workspace.overview.activity.description' | translate }}</p>
                </div>

                @if (recentActivity$ | async; as activity) {
                  <div class="employee-activity">
                    @for (item of activity; track item.title + item.meta) {
                      <div class="employee-activity__item">
                        <div class="employee-activity__dot"></div>
                        <div class="employee-activity__copy">
                          <strong>{{ item.title }}</strong>
                          <span>{{ item.detail }}</span>
                          <small>{{ item.meta }}</small>
                        </div>
                      </div>
                    } @empty {
                      <div class="employee-empty-state">{{ 'users.workspace.fallbacks.noRecentActivity' | translate }}
                      </div>
                    }
                  </div>
                }
              </article>
            </div>
          }
          @default {
            @if (workspaceTab() === 'schedule') {
              <app-employee-schedules [user]="user()" [shifts]="shiftDetails()"
                [initialShiftDate]="initialShiftDate()" [initialShiftSiteGuid]="initialShiftSiteGuid()"
                (schedulesChanged)="onSchedulesChanged()"/>
            } @else if (workspaceTab() === 'time') {
              <app-employee-workforce [employee]="user().guid" mode="time"/>
            } @else if (workspaceTab() === 'documents') {
              <div class="employee-workspace__documents">
                <article class="employee-card employee-card--full">
                  <div class="employee-documents__summary">
                    <div class="employee-documents__summary-chip">
                      <span>{{ 'users.workspace.documents.summary.total' | translate }}</span>
                      <strong>{{ documentsVm().keyDocuments.length - documentsVm().missingDocuments.length + documentsVm().otherDocuments.length }}</strong>
                    </div>
                    <div class="employee-documents__summary-chip">
                      <span>{{ 'users.workspace.documents.summary.expiring' | translate }}</span>
                      <strong>0</strong>
                    </div>
                    <div class="employee-documents__summary-chip">
                      <span>{{ 'users.workspace.documents.summary.missing' | translate }}</span>
                      <strong>{{ documentsVm().missingDocuments.length }}</strong>
                    </div>
                    <button class="profile-button profile-button--primary employee-documents__summary-button"
                      type="button"
                      (click)="addEmployeeDocument()">
                      {{ 'users.workspace.documents.actions.add' | translate }}
                    </button>
                  </div>
                </article>

                <div class="employee-workspace__grid">
                  <article class="employee-card employee-card--full">
                    <div class="employee-card__header">
                      <h2>{{ 'users.workspace.documents.key.title' | translate }}</h2>
                      <p>{{ 'users.workspace.documents.key.description' | translate }}</p>
                    </div>

                    <div class="employee-document-list">
                      @for (document of documentsVm().keyDocuments; track document.key) {
                        <div class="employee-document-row">
                          <div class="employee-document-row__main">
                            <div class="employee-document-row__title">
                              <strong>{{ document.name }}</strong>
                              <div class="employee-status-chip"
                                [class.employee-status-chip--active]="document.status === 'valid'"
                                [class.employee-status-chip--ended]="document.status === 'missing'">
                                {{ ('users.workspace.documents.status.' + document.status) | translate }}
                              </div>
                            </div>
                            <div class="employee-document-row__meta">
                              <span>{{ ('users.workspace.documents.category.' + document.category) | translate }}</span>
                              <span>{{ document.uploadedDate }}</span>
                              <span>{{ document.uploader }}</span>
                            </div>
                          </div>

                          <div class="employee-document-row__actions">
                            @if (document.url) {
                              <button class="profile-button profile-button--ghost"
                                type="button"
                                (click)="openDocument(document.url)">
                                {{ 'users.workspace.documents.actions.open' | translate }}
                              </button>
                              <button class="profile-button profile-button--ghost"
                                type="button"
                                (click)="removeDocument(document.key)">
                                {{ 'users.workspace.documents.actions.remove' | translate }}
                              </button>
                            } @else {
                              <button class="profile-button profile-button--ghost"
                                type="button"
                                (click)="addEmployeeDocument(document.key)">
                                {{ 'users.workspace.documents.actions.addOne' | translate }}
                              </button>
                            }
                          </div>
                        </div>
                      }
                    </div>
                  </article>

                  <article class="employee-card">
                    <div class="employee-card__header">
                      <h2>{{ 'users.workspace.documents.other.title' | translate }}</h2>
                      <p>{{ 'users.workspace.documents.other.description' | translate }}</p>
                    </div>

                    @if (documentsVm().otherDocuments.length > 0) {
                      <div class="employee-document-list">
                        @for (document of documentsVm().otherDocuments; track document.key + document.name) {
                          <div class="employee-document-row">
                            <div class="employee-document-row__main">
                              <div class="employee-document-row__title">
                                <strong>{{ document.name }}</strong>
                                <div class="employee-status-chip employee-status-chip--active">
                                  {{ 'users.workspace.documents.status.valid' | translate }}
                                </div>
                              </div>
                              <div class="employee-document-row__meta">
                                <span>{{ ('users.workspace.documents.category.' + document.category) | translate }}</span>
                                <span>{{ document.uploadedDate }}</span>
                                <span>{{ document.uploader }}</span>
                              </div>
                            </div>

                            <div class="employee-document-row__actions">
                              <button class="profile-button profile-button--ghost"
                                type="button"
                                (click)="openDocument(document.url)">
                                {{ 'users.workspace.documents.actions.open' | translate }}
                              </button>
                              <button class="profile-button profile-button--ghost"
                                type="button"
                                (click)="removeDocument(document.key)">
                                {{ 'users.workspace.documents.actions.remove' | translate }}
                              </button>
                            </div>
                          </div>
                        }
                      </div>
                    } @else {
                      <div class="employee-empty-state">
                        {{ 'users.workspace.documents.fallbacks.noDocuments' | translate }}
                      </div>
                    }
                  </article>

                  <article class="employee-card">
                    <div class="employee-card__header">
                      <h2>{{ 'users.workspace.documents.missing.title' | translate }}</h2>
                      <p>{{ 'users.workspace.documents.missing.description' | translate }}</p>
                    </div>

                    @if (documentsVm().missingDocuments.length > 0) {
                      <div class="employee-list">
                        @for (document of documentsVm().missingDocuments; track document.key) {
                          <div class="employee-list__item employee-list__item--warning">
                            <strong>{{ document.name }}</strong>
                            <span>{{ 'users.workspace.documents.missing.requiredMissing' | translate }}</span>
                          </div>
                        }
                      </div>
                    } @else {
                      <div class="employee-empty-state">
                        {{ 'users.workspace.documents.fallbacks.noMissing' | translate }}
                      </div>
                    }
                  </article>
                </div>
              </div>
            } @else if (workspaceTab() === 'settings') {
              <div class="employee-workspace__settings">
                @if (settingsError()) {
                  <div class="employee-settings__error">
                    {{ settingsError() }}
                  </div>
                }

                <div class="employee-workspace__grid">
                  <article class="employee-card">
                    <div class="employee-card__header employee-settings__header">
                      <div>
                        <h2>{{ 'users.workspace.settings.basic.title' | translate }}</h2>
                        <p>{{ 'users.workspace.settings.basic.description' | translate }}</p>
                      </div>
                      @if (!basicEdit()) {
                        <button class="employee-text-action"
                          type="button"
                          (click)="startBasicEdit()">
                          {{ 'general.edit' | translate }}
                        </button>
                      }
                    </div>

                    @if (basicEdit()) {
                      <div class="employee-settings__form">
                        <label class="employee-settings__field">
                          <span>{{ 'users.workspace.settings.labels.name' | translate }}</span>
                          <input class="employee-settings__control"
                            type="text"
                            [value]="basicDraft().displayName"
                            (input)="updateBasicDraft('displayName', $any($event.target).value)" />
                        </label>
                        <label class="employee-settings__field">
                          <span>{{ 'users.workspace.settings.labels.email' | translate }}</span>
                          <input class="employee-settings__control"
                            type="email"
                            [value]="basicDraft().email"
                            (input)="updateBasicDraft('email', $any($event.target).value)" />
                        </label>
                        <label class="employee-settings__field">
                          <span>{{ 'users.workspace.settings.labels.phone' | translate }}</span>
                          <input class="employee-settings__control"
                            type="text"
                            [value]="basicDraft().phone"
                            (input)="updateBasicDraft('phone', $any($event.target).value)" />
                        </label>
                        <label class="employee-settings__field">
                          <span>{{ 'users.workspace.settings.labels.role' | translate }}</span>
                          <app-select-popover class="employee-settings__control"
                            [value]="basicDraft().role"
                            (change)="updateBasicDraft('role', $any($event.target).value)">
                            <ion-select-option value="admin">{{ 'users.roles.admin' | translate }}</ion-select-option>
                            <ion-select-option
                              value="supervisor">{{ 'users.roles.supervisor' | translate }}</ion-select-option>
                            <ion-select-option value="worker">{{ 'users.roles.worker' | translate }}</ion-select-option>
                            <ion-select-option
                              value="manager">{{ 'users.roles.manager' | translate }}</ion-select-option>
                            <ion-select-option
                              value="calendar">{{ 'users.roles.calendar' | translate }}</ion-select-option>
                            <ion-select-option
                              value="storage">{{ 'users.roles.storage' | translate }}</ion-select-option>
                          </app-select-popover>
                        </label>

                        <div class="employee-settings__actions">
                          <button class="profile-button profile-button--ghost"
                            type="button"
                            (click)="cancelBasicEdit()">
                            {{ 'general.cancel' | translate }}
                          </button>
                          <button class="profile-button profile-button--primary"
                            type="button"
                            (click)="saveBasicInfo()">
                            {{ 'general.save' | translate }}
                          </button>
                        </div>
                      </div>
                    } @else {
                      <div class="employee-card__details employee-card__details--summary">
                        <div>
                          <span>{{ 'users.workspace.settings.labels.name' | translate }}</span>
                          <strong>{{ displayNameLabel() }}</strong>
                        </div>
                        <div>
                          <span>{{ 'users.workspace.settings.labels.email' | translate }}</span>
                          <strong>{{ emailLabel() }}</strong>
                        </div>
                        <div>
                          <span>{{ 'users.workspace.settings.labels.phone' | translate }}</span>
                          <strong>{{ phoneLabel() }}</strong>
                        </div>
                        <div>
                          <span>{{ 'users.workspace.settings.labels.role' | translate }}</span>
                          <strong>{{ roleLabel() }}</strong>
                        </div>
                        <div>
                          <span>{{ 'users.workspace.settings.labels.status' | translate }}</span>
                          <strong>{{ statusLabel() }}</strong>
                        </div>
                      </div>
                    }
                  </article>

                  <article class="employee-card">
                    <div class="employee-card__header employee-settings__header">
                      <div>
                        <h2>{{ 'users.workspace.settings.organization.title' | translate }}</h2>
                        <p>{{ 'users.workspace.settings.organization.description' | translate }}</p>
                      </div>
                      @if (!organizationEdit()) {
                        <button class="employee-text-action"
                          type="button"
                          (click)="startOrganizationEdit()">
                          {{ 'general.edit' | translate }}
                        </button>
                      }
                    </div>

                    @if (organizationEdit()) {
                      <div class="employee-settings__form">
                        <label class="employee-settings__field">
                          <span>{{ 'users.workspace.settings.labels.team' | translate }}</span>
                          <app-select-popover class="employee-settings__control"
                            [value]="organizationDraft().team"
                            (change)="updateOrganizationDraft('team', $any($event.target).value)">
                            <ion-select-option
                              value="">{{ 'users.workspace.fallbacks.noTeam' | translate }}</ion-select-option>
                            @for (team of teamOptions$ | async; track team) {
                              <ion-select-option [value]="team">{{ team }}</ion-select-option>
                            }
                          </app-select-popover>
                        </label>
                        <label class="employee-settings__field">
                          <span>{{ 'users.workspace.settings.labels.area' | translate }}</span>
                          <app-select-popover class="employee-settings__control"
                            [value]="organizationDraft().area"
                            (change)="updateOrganizationDraft('area', $any($event.target).value)">
                            <ion-select-option
                              value="">{{ 'users.workspace.fallbacks.noArea' | translate }}</ion-select-option>
                            @for (area of areaOptions$ | async; track area) {
                              <ion-select-option [value]="area">{{ area }}</ion-select-option>
                            }
                          </app-select-popover>
                        </label>
                        <label class="employee-settings__field">
                          <span>{{ 'users.workspace.settings.labels.company' | translate }}</span>
                          <input class="employee-settings__control"
                            type="text"
                            [value]="organizationDraft().company"
                            (input)="updateOrganizationDraft('company', $any($event.target).value)" />
                        </label>

                        <div class="employee-settings__actions">
                          <button class="profile-button profile-button--ghost"
                            type="button"
                            (click)="cancelOrganizationEdit()">
                            {{ 'general.cancel' | translate }}
                          </button>
                          <button class="profile-button profile-button--primary"
                            type="button"
                            (click)="saveOrganizationInfo()">
                            {{ 'general.save' | translate }}
                          </button>
                        </div>
                      </div>
                    } @else {
                      <div class="employee-card__details employee-card__details--summary">
                        <div>
                          <span>{{ 'users.workspace.settings.labels.team' | translate }}</span>
                          <strong>{{ teamLabel() }}</strong>
                        </div>
                        <div>
                          <span>{{ 'users.workspace.settings.labels.area' | translate }}</span>
                          <strong>{{ areaLabel() }}</strong>
                        </div>
                        <div>
                          <span>{{ 'users.workspace.settings.labels.manager' | translate }}</span>
                          <strong>{{ 'users.workspace.settings.fallbacks.notDefined' | translate }}</strong>
                        </div>
                        <div>
                          <span>{{ 'users.workspace.settings.labels.company' | translate }}</span>
                          <strong>{{ companyLabel() }}</strong>
                        </div>
                      </div>
                    }
                  </article>

                  <article class="employee-card">
                    <div class="employee-card__header employee-settings__header">
                      <div>
                        <h2>{{ 'users.workspace.settings.employment.title' | translate }}</h2>
                        <p>{{ 'users.workspace.settings.employment.description' | translate }}</p>
                      </div>
                      @if (!employmentEdit()) {
                        <button class="employee-text-action"
                          type="button"
                          (click)="startEmploymentEdit()">
                          {{ 'general.edit' | translate }}
                        </button>
                      }
                    </div>

                    @if (employmentEdit()) {
                      <div class="employee-settings__form">
                        <label class="employee-settings__field">
                          <span>{{ 'users.workspace.settings.labels.employmentType' | translate }}</span>
                          <app-select-popover class="employee-settings__control"
                            [value]="employmentDraft().salaryOrInvoice"
                            (change)="updateEmploymentDraft('salaryOrInvoice', $any($event.target).value)">
                            <ion-select-option
                              value="">{{ 'users.workspace.settings.fallbacks.notDefined' | translate }}</ion-select-option>
                            <ion-select-option
                              value="salary">{{ 'users.workspace.settings.employment.salary' | translate }}
                            </ion-select-option>
                            <ion-select-option
                              value="invoice">{{ 'users.workspace.settings.employment.invoice' | translate }}
                            </ion-select-option>
                          </app-select-popover>
                        </label>
                        @if (employmentDraft().salaryOrInvoice === 'salary') {
                          <label class="employee-settings__field">
                            <span>{{ 'users.workspace.settings.labels.workType' | translate }}</span>
                            <app-select-popover class="employee-settings__control"
                              [value]="employmentDraft().workType"
                              (change)="updateEmploymentDraft('workType', $any($event.target).value)">
                              <ion-select-option
                                value="">{{ 'users.workspace.settings.fallbacks.notDefined' | translate }}</ion-select-option>
                              @for (type of workTypeOptions; track type) {
                                <ion-select-option
                                  [value]="type">{{ 'users.workTypes.' + type | translate }}</ion-select-option>
                              }
                            </app-select-popover>
                          </label>
                          <label class="employee-settings__field">
                            <span>{{ 'users.hourlyRate' | translate }}</span>
                            <input class="employee-settings__control"
                              type="number"
                              [value]="employmentDraft().hourlyRate ?? ''"
                              (input)="onEmploymentHourlyRate($any($event.target).value)" />
                          </label>
                          <label class="employee-settings__field">
                            <span>{{ 'users.sideCost' | translate }}</span>
                            <input class="employee-settings__control"
                              type="number"
                              [value]="employmentDraft().sideCost ?? ''"
                              (input)="updateEmploymentDraft('sideCost', $any($event.target).value === '' ? null : +$any($event.target).value)" />
                          </label>
                          <label class="employee-settings__field">
                            <span>{{ 'users.tes' | translate }}</span>
                            <app-select-popover class="employee-settings__control"
                              [value]="employmentDraft().tes"
                              (change)="updateEmploymentDraft('tes', $any($event.target).value)">
                              <ion-select-option
                                value="">{{ 'users.workspace.settings.fallbacks.notDefined' | translate }}</ion-select-option>
                              @for (item of tesOptions; track item) {
                                <ion-select-option
                                  [value]="item">{{ 'users.teses.' + item | translate }}</ion-select-option>
                              }
                            </app-select-popover>
                          </label>
                        } @else if (employmentDraft().salaryOrInvoice === 'invoice') {
                          <label class="employee-settings__field">
                            <span>{{ 'users.hourlyRateAlv0' | translate }}</span>
                            <input class="employee-settings__control"
                              type="number"
                              [value]="employmentDraft().hourlyRate ?? ''"
                              (input)="onEmploymentHourlyRate($any($event.target).value)" />
                          </label>
                          <label class="employee-settings__field">
                            <span>{{ 'users.hourlyRateAlv24' | translate }}</span>
                            <input class="employee-settings__control"
                              type="number"
                              [value]="hourlyRate24()"
                              readonly />
                          </label>
                          <label class="employee-settings__field">
                            <span>{{ 'users.paymentTerm' | translate }}</span>
                            <app-select-popover class="employee-settings__control"
                              [value]="employmentDraft().paymentTerms"
                              (change)="updateEmploymentDraft('paymentTerms', $any($event.target).value)">
                              <ion-select-option
                                value="">{{ 'users.workspace.settings.fallbacks.notDefined' | translate }}</ion-select-option>
                              @for (item of paymentTermOptions; track item) {
                                <ion-select-option
                                  [value]="item">{{ 'users.paymentTerms.' + item | translate }}</ion-select-option>
                              }
                            </app-select-popover>
                          </label>
                        }

                        <label class="employee-settings__checkbox">
                          <input type="checkbox"
                            [checked]="employmentDraft().inactive"
                            (change)="updateEmploymentDraft('inactive', $any($event.target).checked)" />
                          <span>{{ 'users.workspace.settings.labels.inactive' | translate }}</span>
                        </label>

                        <div class="employee-settings__actions">
                          <button class="profile-button profile-button--ghost"
                            type="button"
                            (click)="cancelEmploymentEdit()">
                            {{ 'general.cancel' | translate }}
                          </button>
                          <button class="profile-button profile-button--primary"
                            type="button"
                            (click)="saveEmploymentInfo()">
                            {{ 'general.save' | translate }}
                          </button>
                        </div>
                      </div>
                    } @else {
                      <div class="employee-card__details employee-card__details--summary">
                        <div>
                          <span>{{ 'users.workspace.settings.labels.employmentType' | translate }}</span>
                          <strong>{{ employmentTypeText() }}</strong>
                        </div>
                        <div>
                          <span>{{ 'users.workspace.settings.labels.startDate' | translate }}</span>
                          <strong>{{ user().creationDate ? (user().creationDate | date:'d.M.yyyy') : ('users.workspace.settings.fallbacks.notAvailable' | translate) }}</strong>
                        </div>
                        <div>
                          <span>{{ 'users.workspace.settings.labels.activity' | translate }}</span>
                          <strong>{{ user().inactive ? ('users.status.inactive' | translate) : ('users.status.active' | translate) }}</strong>
                        </div>
                        <div>
                          <span>{{ 'users.workspace.settings.labels.workType' | translate }}</span>
                          <strong>{{ workerTypeText() }}</strong>
                        </div>

                        @if (user().salaryOrInvoice === 'salary') {
                          <div>
                            <span>{{ 'users.hourlyRate' | translate }}</span>
                            <strong>{{ user().hourlyRate ?? '-' }}</strong>
                          </div>
                          <div>
                            <span>{{ 'users.sideCost' | translate }}</span>
                            <strong>{{ user().sideCost ?? '-' }}</strong>
                          </div>
                          <div>
                            <span>{{ 'users.tes' | translate }}</span>
                            <strong>
                              @if (user().tes) {
                                {{ 'users.teses.' + user().tes | translate }}
                              } @else {
                                -
                              }
                            </strong>
                          </div>
                        } @else if (user().salaryOrInvoice === 'invoice') {
                          <div>
                            <span>{{ 'users.hourlyRateAlv0' | translate }}</span>
                            <strong>{{ user().hourlyRate ?? '-' }}</strong>
                          </div>
                          <div>
                            <span>{{ 'users.hourlyRateAlv24' | translate }}</span>
                            <strong>
                              @if (user().hourlyRate != null) {
                                {{ user().hourlyRate * 1.24 | number:'1.2-2' }}
                              } @else {
                                -
                              }
                            </strong>
                          </div>
                          <div>
                            <span>{{ 'users.paymentTerm' | translate }}</span>
                            <strong>
                              @if (user().paymentTerms) {
                                {{ 'users.paymentTerms.' + user().paymentTerms | translate }}
                              } @else {
                                -
                              }
                            </strong>
                          </div>
                        }
                      </div>
                    }
                  </article>

                  @if (hasNetvisor() || hasProcountor()) {
                    <article class="employee-card">
                      <div class="employee-card__header employee-settings__header">
                        <div>
                          <h2>{{ 'users.workspace.settings.integrations.title' | translate }}</h2>
                          <p>{{ 'users.workspace.settings.integrations.description' | translate }}</p>
                        </div>
                      </div>

                      <div class="employee-settings__form">
                        @if (hasNetvisor()) {
                          @if (netvisorEmployees$ | async; as employees) {
                            <label class="employee-settings__field">
                              <span>{{ 'users.netvisorEmployee' | translate }}</span>
                              <app-select-popover class="employee-settings__control"
                                [value]="integrationDraft().netvisorEmployeeNumber ?? ''"
                                (change)="onNetvisorEmployeeChange($any($event.target).value)">
                                <ion-select-option
                                  value="">{{ 'users.workspace.settings.fallbacks.notDefined' | translate }}</ion-select-option>
                                @for (employee of employees; track employee.netvisorkey) {
                                  <ion-select-option [value]="employee.netvisorkey">
                                    {{ employee.realname }} ({{ employee.personalid?.value }})
                                  </ion-select-option>
                                }
                              </app-select-popover>
                            </label>
                          }
                        }

                        @if (hasProcountor()) {
                          @if (procountorEmployees$ | async; as employees) {
                            <label class="employee-settings__field">
                              <span>{{ 'users.procountorEmployee' | translate }}</span>
                              <app-select-popover class="employee-settings__control"
                                [value]="integrationDraft().procountorEmployeeNumber?.toString() ?? ''"
                                (change)="onProcountorEmployeeChange($any($event.target).value)">
                                <ion-select-option
                                  value="">{{ 'users.workspace.settings.fallbacks.notDefined' | translate }}</ion-select-option>
                                @for (employee of employees; track employee.id) {
                                  <ion-select-option [value]="employee.id">
                                    {{ employee.firstName }} {{ employee.lastName }}
                                    ({{ employee.personNumber ?? employee.ssn }})
                                  </ion-select-option>
                                }
                              </app-select-popover>
                            </label>
                          }
                        }
                      </div>
                    </article>
                  }

                  <article class="employee-card employee-card--full employee-card--secondary">
                    <div class="employee-card__header employee-settings__header">
                      <div>
                        <h2>{{ 'users.workspace.settings.extra.title' | translate }}</h2>
                        <p>{{ 'users.workspace.settings.extra.description' | translate }}</p>
                      </div>
                      @if (!extraEdit()) {
                        <button class="employee-text-action"
                          type="button"
                          (click)="startExtraEdit()">
                          {{ 'general.edit' | translate }}
                        </button>
                      }
                    </div>

                    @if (coordinatesUnavailable()) {<p role="status">{{ 'users.workspace.settings.coordinatesUnavailable' | translate }}</p>}
                    @if (extraEdit()) {
                      <div class="employee-settings__form employee-settings__form--wide">
                        <label class="employee-settings__field employee-settings__field--wide">
                          <span>{{ 'users.workspace.settings.labels.languages' | translate }}</span>
                          <input class="employee-settings__control"
                            type="text"
                            [value]="extraDraft().languages"
                            (input)="updateExtraDraft('languages', $any($event.target).value)"
                            [placeholder]="'users.workspace.settings.extra.languagesPlaceholder' | translate" />
                        </label>
                        <label class="employee-settings__field">
                          <span>{{ 'users.workspace.settings.labels.street' | translate }}</span>
                          <input class="employee-settings__control"
                            type="text"
                            [value]="extraDraft().street"
                            (input)="updateExtraDraft('street', $any($event.target).value)" />
                        </label>
                        <label class="employee-settings__field">
                          <span>{{ 'users.workspace.settings.labels.postalCode' | translate }}</span>
                          <input class="employee-settings__control"
                            type="text"
                            [value]="extraDraft().postalCode"
                            (input)="updateExtraDraft('postalCode', $any($event.target).value)" />
                        </label>
                        <label class="employee-settings__field">
                          <span>{{ 'users.workspace.settings.labels.city' | translate }}</span>
                          <input class="employee-settings__control"
                            type="text"
                            [value]="extraDraft().city"
                            (input)="updateExtraDraft('city', $any($event.target).value)" />
                        </label>

                        <div class="employee-settings__actions">
                          <button class="profile-button profile-button--ghost"
                            type="button"
                            (click)="cancelExtraEdit()">
                            {{ 'general.cancel' | translate }}
                          </button>
                          <button class="profile-button profile-button--primary"
                            type="button"
                            (click)="saveExtraInfo()">
                            {{ 'general.save' | translate }}
                          </button>
                        </div>
                      </div>
                    } @else {
                      <div class="employee-card__details">
                        <div>
                          <span>{{ 'users.workspace.settings.labels.languages' | translate }}</span>
                          <strong>{{ languagesLabel() }}</strong>
                        </div>
                        <div>
                          <span>{{ 'users.workspace.settings.labels.street' | translate }}</span>
                          <strong>{{ streetLabel() }}</strong>
                        </div>
                        <div>
                          <span>{{ 'users.workspace.settings.labels.postalCode' | translate }}</span>
                          <strong>{{ postalCodeLabel() }}</strong>
                        </div>
                        <div>
                          <span>{{ 'users.workspace.settings.labels.city' | translate }}</span>
                          <strong>{{ cityLabel() }}</strong>
                        </div>
                      </div>
                    }
                  </article>

                  <article class="employee-card employee-card--full">
                    <div class="employee-card__header">
                      <h2>{{ 'users.workspace.settings.safe.title' | translate }}</h2>
                      <p>{{ 'users.workspace.settings.safe.description' | translate }}</p>
                    </div>

                    <div class="employee-settings__safe-actions">
                      <button class="profile-button profile-button--ghost"
                        type="button"
                        (click)="disableUser()">
                        {{ 'users.workspace.settings.safe.disable' | translate }}
                      </button>
                    </div>
                  </article>
                </div>
              </div>
            } @else {
              <article class="employee-card employee-card--placeholder">
                <div class="employee-card__header">
                  <h2>{{ currentTabLabel() }}</h2>
                  <p>{{ 'users.workspace.placeholder.description' | translate }}</p>
                </div>
                <div class="employee-empty-state">
                  {{ 'users.workspace.placeholder.body' | translate }}
                </div>
              </article>
            }
          }
        }
      </div>
    </div>
  </section>
</section>
